<template>
  <main class="mcp-server-page">
    <!-- Tab buttons (like monitoring center) -->
    <div class="toolbar">
      <button
        v-for="opt in tabOptions"
        :key="opt.key"
        class="btn"
        :class="{ primary: activeTab === opt.key }"
        @click="switchTab(opt.key)"
      >{{ opt.label }}</button>
    </div>

    <!-- 接入资源概览 -->
    <section class="grid resource-cards">
      <div class="item-card resource-card">
        <div class="card-head">
          <span>Agent</span>
          <span class="resource-tag agent">AGENT</span>
        </div>
        <div class="metric">{{ resourceCounts.agentCount || 0 }}<small>个</small></div>
        <div class="delta muted-text"><small>已接入智能体</small></div>
      </div>
      <div class="item-card resource-card">
        <div class="card-head">
          <span>MCP Server</span>
          <span class="resource-tag mcp">MCP</span>
        </div>
        <div class="metric">{{ resourceCounts.mcpCount || 0 }}<small>个</small></div>
        <div class="delta muted-text"><small>已接入服务</small></div>
      </div>
      <div class="item-card resource-card">
        <div class="card-head">
          <span>Skill</span>
          <span class="resource-tag skill">SKILL</span>
        </div>
        <div class="metric">{{ resourceCounts.skillCount || 0 }}<small>个</small></div>
        <div class="delta muted-text"><small>已接入技能</small></div>
      </div>
    </section>

    <!-- MCP Server panel -->
    <section v-show="activeTab === 'mcp'" class="table-panel">
      <div class="toolbar">
        <input
          v-model="searchQuery"
          placeholder="搜索 MCP 服务..."
          class="search-field"
          @keyup.enter="onSearch"
        />
        <button class="btn" @click="onSearch">搜索</button>
        <button v-if="!readonly" class="btn primary" @click="onAdd">新增</button>
      </div>

      <!-- Card grid -->
      <div class="card-grid">
        <div v-for="(row, i) in servers" :key="i" class="item-card">
          <div class="card-header">
            <div class="card-title">{{ row.name }}</div>
            <el-tag :type="statusType(row.status)" size="mini">{{ statusLabel(row.status) }}</el-tag>
          </div>
          <div class="card-url">
            <code class="url-text">{{ row.url }}</code>
          </div>
          <div class="card-stats">
            <div class="stat-item">
              <div class="stat-value">{{ row.toolCount }}</div>
              <div class="stat-label">工具数</div>
            </div>
            <div class="stat-item">
              <div class="stat-value">{{ statusLabel(row.syncStatus) }}</div>
              <div class="stat-label">同步状态</div>
            </div>
          </div>
          <div v-if="row.aiSummary" class="card-summary">
            {{ row.aiSummary }}
          </div>
          <div class="card-meta">
            <span>创建于 {{ $formatTime(row.createdAt) }}</span>
          </div>
          <div class="card-actions">
            <el-button v-if="!readonly" size="small" type="text" @click="onSync(row)">自动更新</el-button>
            <el-button size="small" type="text" @click="onViewTools(row)">查看工具</el-button>
            <el-button v-if="!readonly" size="small" type="text" @click="onEdit(row)">编辑</el-button>
            <el-button v-if="!readonly" size="small" type="text" class="delete-btn" @click="onDelete(row)">删除</el-button>
          </div>
        </div>
      </div>

      <el-pagination
        class="pagination-bar"
        layout="total, prev, pager, next"
        small
        :total="total"
        :page-size="pageSize"
        :current-page.sync="currentPage"
        @current-change="onPageChange"
      />
    </section>

    <!-- Skills panel -->
    <section v-show="activeTab === 'skills'" class="table-panel">
      <div class="toolbar">
        <input
          v-model="skillSearchQuery"
          placeholder="搜索 Skill..."
          class="search-field"
          @keyup.enter="onSkillSearch"
        />
        <button class="btn" @click="onSkillSearch">搜索</button>
        <button v-if="!readonly" class="btn primary" @click="$refs.skillUpload.click()">上传 Skill</button>
        <input ref="skillUpload" type="file" multiple accept=".md" style="display:none" @change="onSkillUpload" />
      </div>

      <!-- Card grid -->
      <div class="card-grid">
        <div v-for="(row, i) in skills" :key="i" class="item-card">
          <div class="card-header">
            <div class="card-title">{{ row.name }}</div>
          </div>
          <div class="card-desc">
            {{ row.description }}
          </div>
          <div class="card-stats">
            <div class="stat-item">
              <div class="stat-value">{{ row.fileName }}</div>
              <div class="stat-label">文件</div>
            </div>
            <div class="stat-item">
              <div class="stat-value">{{ formatFileSize(row.fileSize) }}</div>
              <div class="stat-label">大小</div>
            </div>
          </div>
          <div v-if="row.aiSummary" class="card-summary">
            {{ row.aiSummary }}
          </div>
          <div class="card-meta">
            <span>创建于 {{ $formatTime(row.createdAt) }}</span>
          </div>
          <div class="card-actions">
            <el-button v-if="!readonly" size="small" type="text" @click="onEditSkill(row)">编辑</el-button>
            <el-button v-if="!readonly" size="small" type="text" @click="onAnalyzeSkill(row)">AI 分析</el-button>
            <el-button size="small" type="text" @click="onPreviewSkill(row)">预览</el-button>
            <el-button v-if="!readonly" size="small" type="text" @click="onUpdateSkillFile(row)">更新文件</el-button>
            <el-button size="small" type="text" @click="onDownloadSkill(row)">下载</el-button>
            <el-button v-if="!readonly" size="small" type="text" class="delete-btn" @click="onDeleteSkill(row)">删除</el-button>
          </div>
        </div>
      </div>

      <el-pagination
        class="pagination-bar"
        layout="total, prev, pager, next"
        small
        :total="skillTotal"
        :page-size="skillPageSize"
        :current-page.sync="skillCurrentPage"
        @current-change="onSkillPageChange"
      />
    </section>

    <!-- Agent panel -->
    <section v-show="activeTab === 'agent'" class="table-panel">
      <div class="toolbar">
        <input
          v-model="agentSearchQuery"
          placeholder="搜索 Agent..."
          class="search-field"
          @keyup.enter="onAgentSearch"
        />
        <button class="btn" @click="onAgentSearch">搜索</button>
        <button v-if="!readonly" class="btn primary" @click="onAddAgent">新增 Agent</button>
      </div>

      <!-- Card grid -->
      <div class="card-grid">
        <div v-for="(row, i) in agents" :key="i" class="item-card">
          <div class="card-header">
            <div class="card-title">{{ row.name }}</div>
            <el-tag :type="row.accountId ? 'success' : 'info'" size="mini">
              {{ row.accountId ? '已开通' : '未开通' }}
            </el-tag>
          </div>
          <div class="card-desc">
            {{ row.description || '暂无描述' }}
          </div>
          <div class="card-stats" v-if="row.businessDomain || row.deptName">
            <div class="stat-item" v-if="row.businessDomain">
              <div class="stat-value">{{ row.businessDomain }}</div>
              <div class="stat-label">业务领域</div>
            </div>
            <div class="stat-item" v-if="row.deptName">
              <div class="stat-value">{{ row.deptName }}</div>
              <div class="stat-label">归属部门</div>
            </div>
          </div>
          <div class="card-meta">
            <span>创建于 {{ $formatTime(row.createdAt) }}</span>
          </div>
          <div class="card-actions">
            <el-button v-if="row.accountId" size="small" type="text" @click="onShowAccessInfo(row)">调用接入</el-button>
            <el-button v-if="row.accountId" size="small" type="text" @click="onShowBills(row)">账单</el-button>
            <el-button v-if="!row.accountId && !readonly" size="small" type="text" @click="onProvisionAgent(row)">开通账号</el-button>
            <el-button v-if="!readonly" size="small" type="text" @click="onEditAgent(row)">编辑</el-button>
            <el-button v-if="!readonly" size="small" type="text" class="delete-btn" @click="onDeleteAgent(row)">删除</el-button>
          </div>
        </div>
      </div>

      <el-pagination
        class="pagination-bar"
        layout="total, prev, pager, next"
        small
        :total="agentTotal"
        :page-size="agentPageSize"
        :current-page.sync="agentCurrentPage"
        @current-change="onAgentPageChange"
      />
    </section>

    <!-- Add/Edit Dialog -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="520px" :close-on-click-modal="false">
      <el-form ref="form" :model="form" :rules="rules" label-width="100px" label-position="right" size="small">
        <el-form-item label="URL" prop="url">
          <el-input v-model="form.url" placeholder="MCP Server 地址，例: http://localhost:8080/mcp" />
        </el-form-item>
        <el-form-item label="Token" prop="bearerToken">
          <el-input v-model="form.bearerToken" placeholder="可选，用于认证" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="small" @click="dialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="submitting" @click="submitForm">确定</el-button>
      </span>
    </el-dialog>

    <!-- Tools Dialog -->
    <el-dialog title="MCP 工具列表" :visible.sync="toolsDialogVisible" width="800px" :close-on-click-modal="false">
      <div v-if="toolsDialogVisible">
        <div v-if="currentServer.aiSummary" class="ai-summary-box">
          <strong>概述：</strong>{{ currentServer.aiSummary }}
        </div>
        <el-table :data="tools" size="small" style="width: 100%" max-height="500">
          <el-table-column type="index" label="序号" width="60" />
          <el-table-column prop="name" label="工具名称" width="200" />
          <el-table-column prop="description" label="描述" min-width="300">
            <template slot-scope="scope">
              <span class="tool-desc">{{ scope.row.description || '无描述' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="参数" width="120">
            <template slot-scope="scope">
              <el-button size="mini" type="text" @click="onViewSchema(scope.row)">查看</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-dialog>

    <!-- Schema Dialog -->
    <el-dialog title="工具参数 Schema" :visible.sync="schemaDialogVisible" width="600px">
      <pre class="schema-pre">{{ schemaContent }}</pre>
    </el-dialog>

    <!-- Skill Edit Dialog -->
    <el-dialog title="编辑 Skill" :visible.sync="skillEditVisible" width="520px" :close-on-click-modal="false">
      <el-form ref="skillEditForm" :model="skillEditForm" label-width="100px" label-position="right" size="small">
        <el-form-item label="名称" prop="name">
          <el-input v-model="skillEditForm.name" placeholder="Skill 名称" />
        </el-form-item>
        <el-form-item label="描述" prop="description">
          <el-input v-model="skillEditForm.description" type="textarea" :rows="3" placeholder="Skill 描述" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="small" @click="skillEditVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="skillEditing" @click="submitSkillEdit">确定</el-button>
      </span>
    </el-dialog>

    <!-- Skill Preview Dialog -->
    <el-dialog title="Skill 内容预览" :visible.sync="skillPreviewVisible" width="800px">
      <div v-if="skillPreviewVisible">
        <div class="skill-preview-header">
          <div class="skill-preview-name">{{ currentSkill.name }}</div>
          <div class="skill-preview-desc">{{ currentSkill.description }}</div>
        </div>
        <div v-if="currentSkill.aiSummary" class="skill-preview-summary">
          <strong>概述：</strong>{{ currentSkill.aiSummary }}
        </div>
        <pre class="skill-preview-content">{{ skillPreviewContent }}</pre>
      </div>
    </el-dialog>

    <!-- Agent Add/Edit Dialog -->
    <el-dialog :title="agentDialogTitle" :visible.sync="agentDialogVisible" width="640px" :close-on-click-modal="false">
      <el-form ref="agentForm" :model="agentForm" :rules="agentRules" label-width="100px" label-position="right" size="small">
        <el-form-item label="名称" prop="name">
          <el-input v-model="agentForm.name" placeholder="Agent 名称" />
        </el-form-item>
        <el-form-item label="描述" prop="description">
          <el-input v-model="agentForm.description" type="textarea" :rows="3" placeholder="Agent 功能描述（可选）" />
        </el-form-item>
        <el-form-item label="业务领域">
          <el-input v-model="agentForm.businessDomain" placeholder="如 customs、finance" />
        </el-form-item>
        <el-form-item label="归属部门">
          <el-input v-model="agentForm.deptName" placeholder="如 技术部" />
        </el-form-item>
        <el-form-item label="核心价值分">
          <el-input-number v-model="agentForm.kvScore" :min="0" :max="100" :precision="0" style="width:160px" />
        </el-form-item>
        <el-form-item label="优化增益分">
          <el-input-number v-model="agentForm.obScore" :min="0" :precision="2" style="width:160px" />
        </el-form-item>
        <el-form-item label="优化类型">
          <el-select v-model="agentForm.obType" placeholder="选择类型" clearable style="width:100%">
            <el-option label="节省工时" value="time_saved" />
            <el-option label="成本降低" value="cost_reduction" />
            <el-option label="错误率降低" value="error_reduction" />
            <el-option label="风险降低" value="risk_reduction" />
            <el-option label="质量提升" value="quality_improvement" />
            <el-option label="人工估算" value="manual_estimation" />
            <el-option label="其他" value="other" />
          </el-select>
        </el-form-item>
        <el-form-item label="收益描述">
          <el-input v-model="agentForm.obDescription" type="textarea" :rows="2" placeholder="描述优化收益" />
        </el-form-item>
        <el-form-item>
          <div style="margin-bottom:6px;font-size:13px;color:rgba(255,255,255,0.6);font-weight:600;">分配模型</div>
          <el-checkbox-group v-model="agentForm.modelIds" :disabled="agentModelDisabled">
            <el-checkbox v-for="m in agentAvailableModels" :key="m.supplierModelId" :label="m.supplierModelId" style="margin-right:12px;margin-bottom:6px;">
              {{ m.name }}
              <el-tag v-if="m.offlineAt" size="mini" type="warning" style="margin-left:4px;">即将下架</el-tag>
            </el-checkbox>
          </el-checkbox-group>
          <div v-if="agentModelDisabled" style="font-size:12px;color:rgba(255,255,255,0.4);">开通账号后可分配模型</div>
          <div v-else-if="agentAvailableModels.length === 0" style="font-size:12px;color:rgba(255,255,255,0.4);">暂无可分配模型</div>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="small" @click="agentDialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="agentSubmitting" @click="submitAgentForm">确定</el-button>
      </span>
    </el-dialog>

    <!-- Agent 开通账号 Dialog -->
    <el-dialog title="开通账号" :visible.sync="provisionDialogVisible" width="420px">
      <el-form label-width="70px" @submit.native.prevent>
        <el-form-item label="账号">
          <el-input v-model="provisionForm.username" placeholder="请输入账号名" />
          <div style="font-size:12px;color:rgba(255,255,255,0.4);margin-top:4px;">最终账号将自动加 <code>-agent</code> 后缀</div>
        </el-form-item>
        <el-form-item label="密码">
          <el-input v-model="provisionForm.password" type="password" placeholder="至少 6 位" show-password />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="small" @click="provisionDialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="provisionSubmitting" @click="submitProvision">确定</el-button>
      </span>
    </el-dialog>

    <!-- Agent 调用接入 Dialog -->
    <el-dialog title="Agent 调用接入" :visible.sync="agentKeyDialogVisible" width="560px">
      <div class="access-section">
        <div class="access-label">API Key</div>
        <div class="access-value">
          <code>{{ currentAgentApiKey }}</code>
          <el-button size="mini" type="text" @click="copyText(currentAgentApiKey)">复制</el-button>
        </div>
      </div>
      <div class="access-section">
        <div class="access-label">
          测试命令
          <span class="access-tip">已包含 API Key，可直接运行</span>
        </div>
        <div class="access-curl">
          <pre><code>{{ currentAgentCurl }}</code></pre>
          <el-button size="mini" type="text" @click="copyText(currentAgentCurl)">复制</el-button>
        </div>
      </div>
      <div class="access-tip" style="margin-top: 12px">
        请妥善保管 API Key，用于 Agent 调用本平台 API 时的身份认证。
      </div>
    </el-dialog>

    <!-- Agent 账单 Dialog -->
    <AgentBillsDialog
      :visible.sync="agentBillsVisible"
      :agentId="agentBillsId"
      :agentName="agentBillsName"
    />
  </main>
</template>

<script>
import AgentBillsDialog from '@/components/AgentBillsDialog.vue'

export default {
  name: 'McpServerPage',
  components: { AgentBillsDialog },
  data() {
    return {
      activeTab: 'agent',
      resourceCounts: { mcpCount: 0, skillCount: 0, agentCount: 0 },
      servers: [],
      total: 0,
      pageSize: 10,
      currentPage: 1,
      searchQuery: '',
      dialogVisible: false,
      dialogTitle: '',
      rules: {
        url: [{ required: true, message: '请输入 URL', trigger: 'blur' }]
      },
      submitting: false,
      form: {
        id: null,
        url: '',
        bearerToken: ''
      },
      toolsDialogVisible: false,
      currentServer: {},
      tools: [],
      schemaDialogVisible: false,
      schemaContent: '',
      // Skill data
      skills: [],
      skillTotal: 0,
      skillPageSize: 10,
      skillCurrentPage: 1,
      skillSearchQuery: '',
      skillPreviewVisible: false,
      currentSkill: {},
      skillPreviewContent: '',
      skillEditVisible: false,
      skillEditForm: { id: null, name: '', description: '' },
      skillEditing: false,
      // Agent data
      agents: [],
      agentTotal: 0,
      agentPageSize: 20,
      agentCurrentPage: 1,
      agentSearchQuery: '',
      agentDialogVisible: false,
      agentDialogTitle: '',
      agentSubmitting: false,
      agentAvailableModels: [],
      agentModelDisabled: true,
      agentForm: { id: null, name: '', description: '', modelIds: [], businessDomain: '', deptName: '', kvScore: 0, obScore: 0, obType: '', obDescription: '' },
      agentRules: {
        name: [{ required: true, message: '请输入 Agent 名称', trigger: 'blur' }]
      },
      agentKeyDialogVisible: false,
      currentAgent: {},
      currentAgentApiKey: '',
      currentAgentCurl: '',
      provisionDialogVisible: false,
      provisionAgentId: null,
      provisionAgentRow: null,
      provisionSubmitting: false,
      provisionForm: { username: '', password: '' },
      agentBillsVisible: false,
      agentBillsId: null,
      agentBillsName: '',
      readonly: false
    }
  },
  computed: {
    tabOptions() {
      return [
        { key: 'agent', label: 'Agent' },
        { key: 'mcp', label: 'MCP Server' },
        { key: 'skills', label: 'Skills' }
      ]
    }
  },
  mounted() {
    this.fetchAgents()
    this.fetchAgentAvailableModels()
    this.fetchResourceCounts()
  },
  methods: {
    switchTab(key) {
      this.activeTab = key
      if (key === 'agent') {
        this.fetchAgents()
        this.fetchAgentAvailableModels()
      } else if (key === 'mcp') {
        this.fetchServers()
      } else if (key === 'skills') {
        this.fetchSkills()
      }
    },
    async fetchServers() {
      if (this.activeTab !== 'mcp') return
      try {
        const res = await this.$http.get('/admin/mcp-servers', {
          params: {
            keyword: this.searchQuery || undefined,
            page: this.currentPage,
            pageSize: this.pageSize
          }
        })
        this.servers = res.records || []
        this.total = Number(res.total) || 0
      } catch {
        this.$message.error('加载 MCP 服务列表失败')
      }
    },
    async fetchResourceCounts() {
      try {
        const res = await this.$http.get('/admin/mcp-servers/counts')
        this.resourceCounts = res
        this.readonly = !!res.readonly
      } catch {
        // silent
      }
    },
    onPageChange(page) {
      this.currentPage = page
      this.fetchServers()
    },
    onSearch() {
      this.currentPage = 1
      this.fetchServers()
    },
    onAdd() {
      this.dialogTitle = '新增 MCP 服务'
      this.form = { id: null, url: '', bearerToken: '' }
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.form?.clearValidate())
    },
    onEdit(row) {
      this.dialogTitle = '编辑 MCP 服务'
      this.form = { id: row.id, url: row.url, bearerToken: row.bearerToken }
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.form?.clearValidate())
    },
    submitForm() {
      this.$refs.form.validate(async valid => {
        if (!valid) return
        this.submitting = true
        try {
          if (this.form.id) {
            await this.$http.put('/admin/mcp-servers/' + this.form.id, this.form)
            this.$message.success('编辑成功')
          } else {
            await this.$http.post('/admin/mcp-servers', this.form)
            this.$message.success('新增成功')
          }
          this.dialogVisible = false
          this.fetchServers()
        } catch {
          this.$message.error('操作失败')
        } finally {
          this.submitting = false
        }
      })
    },
    onSync(row) {
      this.$confirm('确定自动更新 MCP 服务「' + row.name + '」的工具列表？', '自动更新工具', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'info'
      }).then(async () => {
        try {
          this.$message.info('正在自动更新...')
          await this.$http.post('/admin/mcp-servers/' + row.id + '/sync')
          this.$message.success('自动更新成功')
          this.fetchServers()
        } catch (e) {
          this.$message.error('自动更新失败: ' + (e.response?.data?.message || e.message))
        }
      }).catch(() => {})
    },
    async onViewTools(row) {
      this.currentServer = row
      try {
        const res = await this.$http.get('/admin/mcp-servers/' + row.id)
        this.tools = res.tools || []
        this.toolsDialogVisible = true
      } catch {
        this.$message.error('加载工具失败')
      }
    },
    onViewSchema(row) {
      this.schemaContent = row.inputSchema || '无参数'
      this.schemaDialogVisible = true
    },
    onDelete(row) {
      this.$confirm('确定删除 MCP 服务「' + row.name + '」？', '确认删除', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        return this.$http.delete('/admin/mcp-servers/' + row.id)
      }).then(() => {
        this.$message.success('删除成功')
        this.fetchServers()
      }).catch(() => {})
    },
    statusType(status) {
      if (status === 'online') return 'success'
      if (status === 'offline') return 'danger'
      return 'info'
    },
    statusLabel(status) {
      if (status === 'online') return '在线'
      if (status === 'offline') return '离线'
      if (status === 'success') return '成功'
      if (status === 'failed') return '失败'
      if (status === 'syncing') return '同步中'
      if (status === 'idle') return '空闲'
      return '未知'
    },
    // Skill methods
    async fetchSkills() {
      if (this.activeTab !== 'skills') return
      try {
        const res = await this.$http.get('/admin/skills', {
          params: {
            keyword: this.skillSearchQuery || undefined,
            page: this.skillCurrentPage,
            pageSize: this.skillPageSize
          }
        })
        this.skills = res.records || []
        this.skillTotal = Number(res.total) || 0
      } catch {
        this.$message.error('加载 Skill 列表失败')
      }
    },
    onSkillPageChange(page) {
      this.skillCurrentPage = page
      this.fetchSkills()
    },
    onSkillSearch() {
      this.skillCurrentPage = 1
      this.fetchSkills()
    },
    async onSkillUpload(event) {
      const files = event.target.files
      if (!files || files.length === 0) return

      const formData = new FormData()
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i])
      }

      try {
        this.$message.info('正在上传并分析...')
        await this.$http.post('/admin/skills/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        this.$message.success('上传成功')
        this.fetchSkills()
      } catch (e) {
        this.$message.error('上传失败: ' + (e.response?.data?.message || e.message))
      } finally {
        // Reset file input
        this.$refs.skillUpload.value = ''
      }
    },
    onEditSkill(row) {
      this.skillEditForm = { id: row.id, name: row.name || '', description: row.description || '' }
      this.skillEditVisible = true
    },
    async submitSkillEdit() {
      this.skillEditing = true
      try {
        await this.$http.put('/admin/skills/' + this.skillEditForm.id, {
          name: this.skillEditForm.name,
          description: this.skillEditForm.description
        })
        this.$message.success('编辑成功')
        this.skillEditVisible = false
        this.fetchSkills()
      } catch {
        this.$message.error('编辑失败')
      } finally {
        this.skillEditing = false
      }
    },
    async onAnalyzeSkill(row) {
      this.$confirm('将对 Skill「' + row.name + '」进行 AI 分析重新生成名称和描述，确定继续？', 'AI 分析', {
        confirmButtonText: '开始分析',
        cancelButtonText: '取消',
        type: 'info'
      }).then(async () => {
        try {
          this.$message.info('正在分析中，请稍候...')
          await this.$http.post('/admin/skills/' + row.id + '/analyze')
          this.$message.success('分析完成')
          this.fetchSkills()
        } catch (e) {
          this.$message.error(e.response?.data?.error || '分析失败')
        }
      }).catch(() => {})
    },
    async onPreviewSkill(row) {
      this.currentSkill = row
      try {
        const res = await this.$http.get('/admin/skills/' + row.id + '/preview', {
          responseType: 'text'
        })
        this.skillPreviewContent = res
        this.skillPreviewVisible = true
      } catch {
        this.$message.error('加载预览失败')
      }
    },
    async onDownloadSkill(row) {
      try {
        const res = await this.$http.get('/admin/skills/' + row.id + '/download', {
          responseType: 'blob'
        })
        const blob = new Blob([res], { type: 'text/plain' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = row.fileName || 'skill.md'
        link.click()
        window.URL.revokeObjectURL(url)
      } catch {
        this.$message.error('下载失败')
      }
    },
    async onUpdateSkillFile(row) {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = '.md'
      input.onchange = async (e) => {
        const file = e.target.files[0]
        if (!file) return

        const formData = new FormData()
        formData.append('file', file)

        try {
          this.$message.info('正在更新并重新分析...')
          await this.$http.put('/admin/skills/' + row.id + '/file', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
          this.$message.success('更新成功')
          this.fetchSkills()
        } catch (err) {
          this.$message.error('更新失败: ' + (err.response?.data?.message || err.message))
        }
      }
      input.click()
    },
    onDeleteSkill(row) {
      this.$confirm('确定删除 Skill「' + row.name + '」？', '确认删除', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        return this.$http.delete('/admin/skills/' + row.id)
      }).then(() => {
        this.$message.success('删除成功')
        this.fetchSkills()
      }).catch(() => {})
    },
    // Agent methods
    async fetchAgents() {
      if (this.activeTab !== 'agent') return
      try {
        const res = await this.$http.get('/admin/agents', {
          params: {
            keyword: this.agentSearchQuery || undefined,
            page: this.agentCurrentPage,
            pageSize: this.agentPageSize
          }
        })
        this.agents = res.records || []
        this.agentTotal = Number(res.total) || 0
      } catch {
        this.$message.error('加载 Agent 列表失败')
      }
    },
    onAgentPageChange(page) {
      this.agentCurrentPage = page
      this.fetchAgents()
    },
    onAgentSearch() {
      this.agentCurrentPage = 1
      this.fetchAgents()
    },
    onAddAgent() {
      this.agentDialogTitle = '新增 Agent'
      this.agentForm = { id: null, name: '', description: '', modelIds: [], businessDomain: '', deptName: '', kvScore: 0, obScore: 0, obType: '', obDescription: '' }
      this.agentModelDisabled = true
      this.agentDialogVisible = true
      this.$nextTick(() => this.$refs.agentForm?.clearValidate())
    },
    async onEditAgent(row) {
      this.agentModelDisabled = !row.accountId
      this.agentDialogTitle = '编辑 Agent'
      this.agentForm = {
        id: row.id,
        name: row.name,
        description: row.description || '',
        modelIds: [],
        businessDomain: row.businessDomain || '',
        deptName: row.deptName || '',
        kvScore: row.kvScore != null ? Number(row.kvScore) : 0,
        obScore: row.obScore != null ? Number(row.obScore) : 0,
        obType: row.obType || '',
        obDescription: row.obDescription || ''
      }
      this.agentDialogVisible = true
      this.$nextTick(() => this.$refs.agentForm?.clearValidate())
      // Load model IDs for this agent
      try {
        const res = await this.$http.get('/admin/agents/' + row.id)
        this.agentForm.modelIds = res.modelIds || []
      } catch {
        // 静默失败
      }
    },
    submitAgentForm() {
      this.$refs.agentForm.validate(async valid => {
        if (!valid) return
        this.agentSubmitting = true
        try {
          const payload = {
            name: this.agentForm.name,
            description: this.agentForm.description,
            businessDomain: this.agentForm.businessDomain || null,
            deptName: this.agentForm.deptName || null,
            kvScore: this.agentForm.kvScore || 0,
            obScore: this.agentForm.obScore || 0,
            obType: this.agentForm.obType || null,
            obDescription: this.agentForm.obDescription || null
          }
          if (this.agentForm.id) {
            // Edit: also pass modelIds for re-association
            payload.modelIds = this.agentForm.modelIds
            await this.$http.put('/admin/agents/' + this.agentForm.id, payload)
            this.$message.success('编辑成功')
          } else {
            await this.$http.post('/admin/agents', payload)
            this.$message.success('新增成功')
          }
          this.agentDialogVisible = false
          this.fetchAgents()
        } catch (e) {
          // 400 错误已由全局拦截器处理
        } finally {
          this.agentSubmitting = false
        }
      })
    },
    async fetchAgentAvailableModels() {
      try {
        const res = await this.$http.get('/admin/customers/models/self', {
          params: { page: 1, pageSize: 1000 }
        })
        this.agentAvailableModels = res.records || []
      } catch {
        // 静默失败
      }
    },
    onShowBills(row) {
      this.agentBillsId = row.id
      this.agentBillsName = row.name || ''
      this.agentBillsVisible = true
    },
    async onShowAccessInfo(row) {
      try {
        const res = await this.$http.get('/admin/agents/' + row.id)
        this.currentAgent = res.agent || row
        this.currentAgentApiKey = res.apiKey || ''
        const apiKey = res.apiKey || ''
        const baseUrl = res.baseUrl || ''
        const modelName = res.modelName || 'gpt-3.5-turbo'
        this.currentAgentCurl = `curl -X POST ${baseUrl}/v1/chat/completions \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -d '{
    "model": "${modelName}",
    "messages": [
      {"role": "user", "content": "你好，请介绍一下你自己"}
    ],
    "temperature": 0.7
  }'`
        this.agentKeyDialogVisible = true
      } catch {
        this.$message.error('获取接入信息失败')
      }
    },
    onProvisionAgent(row) {
      this.provisionAgentId = row.id
      this.provisionAgentRow = row
      this.provisionForm = { username: this.makeUsernameBase(row.name), password: '' }
      this.provisionDialogVisible = true
    },
    async submitProvision() {
      const username = (this.provisionForm.username || '').trim()
      const password = this.provisionForm.password || ''
      if (!username) { this.$message.error('请输入账号名'); return }
      if (password.length < 6) { this.$message.error('密码至少 6 位'); return }
      this.provisionSubmitting = true
      try {
        const res = await this.$http.post('/admin/agents/' + this.provisionAgentId + '/provision', { username, password })
        const row = this.provisionAgentRow
        this.currentAgent = row
        this.currentAgentApiKey = res.apiKey || ''
        const apiKey = res.apiKey || ''
        const baseUrl = res.baseUrl || ''
        const modelName = res.modelName || 'gpt-3.5-turbo'
        this.currentAgentCurl = `curl -X POST ${baseUrl}/v1/chat/completions \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -d '{
    "model": "${modelName}",
    "messages": [
      {"role": "user", "content": "你好，请介绍一下你自己"}
    ],
    "temperature": 0.7
  }'`
        this.provisionDialogVisible = false
        this.agentKeyDialogVisible = true
        this.$message.success('账号开通成功')
        this.fetchAgents()
      } catch (e) {
        this.$message.error('开通失败: ' + (e.response?.data?.error || e.message))
      } finally {
        this.provisionSubmitting = false
      }
    },
    makeUsernameBase(name) {
      const s = (name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
      return s || ''
    },
    copyText(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$message.success('已复制到剪贴板')
      }).catch(() => {
        this.$message.error('复制失败')
      })
    },
    onDeleteAgent(row) {
      this.$confirm('确定删除 Agent「' + row.name + '」？删除后关联的账号也会被禁用。', '确认删除', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        return this.$http.delete('/admin/agents/' + row.id)
      }).then(() => {
        this.$message.success('删除成功')
        this.fetchAgents()
      }).catch((e) => {
        if (e && e.response && e.response.data && e.response.data.error) {
          this.$message.error(e.response.data.error)
        }
      })
    },
    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return '0 B'
      const units = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(1024))
      return (bytes / Math.pow(1024, i)).toFixed(1) + ' ' + units[i]
    }
  }
}
</script>

<style scoped>
.mcp-server-page {
  padding: 0 24px 16px;
}

/* ---------- 接入资源概览 ---------- */
.resource-cards {
  display: grid;
  grid-template-columns: repeat(3, minmax(150px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
}
.resource-card {
  min-height: 100px;
}
.resource-card .card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--muted);
  font-size: 13px;
  font-weight: 760;
}
.resource-card .metric {
  margin-top: 10px;
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: clamp(20px, 2.2vw, 28px);
  line-height: 1.05;
  font-weight: 900;
  color: var(--text);
}
.resource-card .metric small {
  color: var(--muted);
  font-size: 12px;
  font-weight: 800;
}
.resource-card .delta {
  margin-top: 10px;
  font-size: 12px;
  color: var(--muted);
}
.resource-tag {
  font-size: 9px;
  font-weight: 900;
  letter-spacing: 0.06em;
  padding: 2px 6px;
  border-radius: 4px;
  line-height: 1.4;
}
.resource-tag.mcp {
  background: rgba(50, 200, 245, 0.18);
  color: #32c8f5;
  border: 1px solid rgba(50, 200, 245, 0.35);
}
.resource-tag.skill {
  background: rgba(22, 217, 152, 0.18);
  color: #16d998;
  border: 1px solid rgba(22, 217, 152, 0.35);
}
.resource-tag.agent {
  background: rgba(139, 120, 255, 0.18);
  color: #8b78ff;
  border: 1px solid rgba(139, 120, 255, 0.35);
}

.table-panel {
  padding: 16px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: linear-gradient(180deg, rgba(27, 39, 58, 0.9), rgba(20, 30, 46, 0.88));
  box-shadow: var(--shadow);
  overflow-x: auto;
}

.card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.item-card {
  padding: 18px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(27, 39, 58, 0.95), rgba(20, 30, 46, 0.9));
  border: 1px solid rgba(133, 164, 255, 0.12);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  display: flex;
  flex-direction: column;
}

.item-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
  border-color: rgba(50, 200, 245, 0.25);
}

.item-card .card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  gap: 8px;
}

.item-card .card-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--text);
  line-height: 1.3;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-card .card-url {
  margin-bottom: 12px;
}

.item-card .card-desc {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.5;
  margin-bottom: 12px;
  min-height: 40px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.item-card .card-stats {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.item-card .stat-item {
  flex: 1;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 8px;
  padding: 10px;
  text-align: center;
}

.item-card .stat-value {
  font-size: 14px;
  font-weight: 700;
  color: var(--text);
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-card .stat-label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
  margin-top: 4px;
}

.item-card .card-summary {
  margin-bottom: 12px;
  padding: 10px;
  background: rgba(50, 200, 245, 0.06);
  border-radius: 8px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.item-card .card-meta {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.35);
  margin-bottom: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(133, 164, 255, 0.08);
}

.item-card .card-actions {
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  gap: 2px;
  padding-top: 10px;
  border-top: 1px solid rgba(133, 164, 255, 0.08);
}

.item-card .card-actions .el-button--text {
  font-size: 12px;
  padding: 4px 6px;
  height: auto;
  white-space: nowrap;
}

.table-panel ::v-deep .el-table,
.table-panel ::v-deep .el-table__header-wrapper,
.table-panel ::v-deep .el-table__body-wrapper {
  border-radius: var(--radius);
}

.table-panel ::v-deep th {
  background: rgba(255, 255, 255, 0.045);
  color: #9eb1ca;
  font-weight: 850;
}

.table-panel ::v-deep td,
.table-panel ::v-deep th {
  border-color: var(--line-soft);
}

.pagination-bar {
  margin-top: 20px;
  text-align: right;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.btn {
  height: 36px;
  padding: 0 13px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: rgba(255, 255, 255, 0.06);
  color: #dbe8fb;
  font: inherit;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.btn:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(50, 200, 245, 0.42);
  color: #fff;
}

.btn.primary {
  border-color: rgba(50, 200, 245, 0.34);
  background: linear-gradient(135deg, #4f86ff, #32bde8);
  color: #fff;
  box-shadow: 0 10px 28px rgba(50, 150, 245, 0.18);
}

.search-field {
  height: 36px;
  padding: 0 12px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: rgba(255, 255, 255, 0.06);
  color: #eaf3ff;
  font: inherit;
  font-size: 13px;
  outline: none;
  width: 220px;
  transition: border-color 0.15s;
}

.search-field::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

.search-field:focus {
  border-color: rgba(50, 200, 245, 0.5);
}

.url-text {
  font-size: 13px;
  color: #8eddf8;
  background: rgba(50, 200, 245, 0.1);
  padding: 2px 8px;
  border-radius: 4px;
}

.ai-summary {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.5;
}

.delete-btn {
  color: #ff93a4;
}

.delete-btn:hover {
  color: #ffb6c1;
}

.ai-summary-box {
  margin-bottom: 16px;
  padding: 12px;
  background: rgba(50, 200, 245, 0.08);
  border-radius: 8px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.6;
}

.tool-desc {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
}

.schema-pre {
  background: rgba(0, 0, 0, 0.3);
  padding: 12px;
  border-radius: 6px;
  font-size: 12px;
  color: #8eddf8;
  overflow-x: auto;
  max-height: 400px;
  white-space: pre-wrap;
  word-break: break-all;
}

.skill-preview-header {
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--line-soft);
}

.skill-preview-name {
  font-size: 18px;
  font-weight: 700;
  color: var(--text);
  margin-bottom: 6px;
}

.skill-preview-desc {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.5;
}

.skill-preview-summary {
  margin-bottom: 16px;
  padding: 12px;
  background: rgba(50, 200, 245, 0.08);
  border-radius: 8px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.6;
}

.skill-preview-content {
  background: rgba(0, 0, 0, 0.3);
  padding: 16px;
  border-radius: 6px;
  font-size: 13px;
  color: #eaf3ff;
  overflow-x: auto;
  max-height: 500px;
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.6;
  font-family: 'Consolas', 'Monaco', monospace;
}

.empty-tab {
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-content {
  text-align: center;
  color: var(--muted);
}

.empty-content i {
  font-size: 48px;
  margin-bottom: 16px;
  display: block;
  opacity: 0.3;
}

.empty-content p {
  font-size: 14px;
  margin: 0;
}

.access-section {
  margin-bottom: 16px;
}
.access-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.access-tip {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
}
.access-value {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 6px;
  border: 1px solid rgba(50, 200, 245, 0.15);
}
.access-value code {
  flex: 1;
  font-size: 13px;
  color: #8eddf8;
  word-break: break-all;
  font-family: 'Consolas', 'Monaco', monospace;
}
.access-curl {
  position: relative;
  background: rgba(0, 0, 0, 0.35);
  border-radius: 6px;
  border: 1px solid rgba(133, 164, 255, 0.15);
  padding: 12px;
  padding-right: 56px;
}
.access-curl pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-all;
}
.access-curl code {
  font-size: 12px;
  color: #c6d4e9;
  font-family: 'Consolas', 'Monaco', monospace;
  line-height: 1.6;
}
.access-curl > .el-button {
  position: absolute;
  top: 8px;
  right: 8px;
}

/* ---------- Responsive ---------- */
@media (max-width: 760px) {
  .mcp-server-page {
    padding: 0 12px 16px;
  }
  .resource-cards {
    grid-template-columns: 1fr 1fr 1fr;
    gap: 6px;
  }
  .resource-card {
    min-height: 80px;
    padding: 12px;
  }
  .resource-card .metric {
    font-size: 16px;
  }
  .table-panel {
    padding: 10px;
  }
  .toolbar {
    flex-wrap: wrap;
  }
  .search-field {
    flex: 1;
    min-width: 80px;
    width: auto;
  }
  .card-grid {
    grid-template-columns: 1fr;
  }
  .pagination-bar {
    text-align: center;
  }
}

@media (max-width: 480px) {
  .item-card {
    padding: 14px;
  }
  .item-card .card-title {
    font-size: 14px;
  }
}
</style>
